import { type ReactNode, useEffect, useRef, useState } from "react";

import { ChevronLeftIcon, ChevronRightIcon } from "lucide-react";

import { styled } from "simplydo/react/styled";
import { useTheme } from "simplydo/react/theme";
import { Button } from "simplydo/react/ui/button";
import { Icon, IconData } from "simplydo/react/ui/icon";
import {
  Modal,
  ModalActions,
  ModalContent,
  ModalHeader,
} from "simplydo/react/ui/modal";

export type ModalCarouselScreen = {
  title: string;
  content: ReactNode;
  actions?: ModalCarouselAction[];
  disableNext?: boolean;
};

export type ModalCarouselAction = {
  label: string;
  onClick: () => void;
  color?: string;
  icon?: IconData;
  primary?: boolean;
  disabled?: boolean;
  loading?: boolean;
};

type ModalCarouselProps = {
  title?: string;
  open: boolean;
  screens: ModalCarouselScreen[];
  actions?: ModalCarouselAction[];
  onClose: () => void;
  scrolling?: boolean;
  initialPage?: number;
  page?: number;
  onPageChange?: (page: number) => void;
};

const StepProgress = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const StepCount = styled.span`
  font-size: 0.85rem;
  color: rgba(0, 0, 0, 0.45);
  font-weight: 500;
  white-space: nowrap;
`;

const StepDots = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const StepDot = styled.button<{
  $active: boolean;
  $completed: boolean;
  $colour: string;
}>`
  border: none;
  padding: 0;
  cursor: pointer;
  transition:
    width 0.2s ease,
    background-color 0.2s ease,
    opacity 0.2s ease;
  height: 8px;
  border-radius: 4px;
  width: ${({ $active }) => ($active ? "24px" : "8px")};
  background-color: ${({ $active, $completed, $colour }) => {
    if ($active) {
      return $colour;
    }
    if ($completed) {
      return $colour;
    }
    return "rgba(0, 0, 0, 0.15)";
  }};
  opacity: ${({ $completed, $active }) => ($completed && !$active ? 0.45 : 1)};

  &:disabled {
    cursor: default;
  }

  &:not(:disabled):hover {
    opacity: 0.85;
  }
`;

const ActionsRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  width: 100%;
`;

const NavGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex: 1;
  min-width: 0;
`;

const ActionGroup = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  flex-wrap: wrap;
  flex-shrink: 0;
`;

type StepDotButtonProps = {
  isActive: boolean;
  isCompleted: boolean;
  colour: string;
  canNavigate: boolean;
  onClick: () => void;
};

const StepDotButton = ({
  isActive,
  isCompleted,
  colour,
  canNavigate,
  onClick,
}: StepDotButtonProps) => (
  <StepDot
    type="button"
    aria-label={isActive ? "Current step" : "Go to step"}
    aria-current={isActive ? "step" : undefined}
    $active={isActive}
    $completed={isCompleted}
    $colour={colour}
    disabled={!canNavigate}
    onClick={onClick}
  />
);

const ModalCarousel = ({
  title,
  open,
  onClose,
  screens,
  actions = [],
  scrolling,
  initialPage = 0,
  page,
  onPageChange,
}: ModalCarouselProps) => {
  const theme = useTheme();
  const [internalPage, setInternalPage] = useState(initialPage);
  const activePage = page ?? internalPage;
  const wasOpenRef = useRef(open);

  const setActivePage = (
    nextPage: number | ((previousPage: number) => number),
  ) => {
    const resolvedPage =
      typeof nextPage === "function" ? nextPage(activePage) : nextPage;
    if (onPageChange) {
      onPageChange(resolvedPage);
    } else {
      setInternalPage(resolvedPage);
    }
  };

  useEffect(() => {
    if (open && !wasOpenRef.current) {
      if (onPageChange) {
        onPageChange(initialPage);
      } else {
        setInternalPage(initialPage);
      }
    }
    wasOpenRef.current = open;
  }, [open, initialPage, onPageChange]);

  useEffect(() => {
    if (activePage >= screens.length && screens.length > 0) {
      const lastPage = screens.length - 1;
      if (onPageChange) {
        onPageChange(lastPage);
      } else {
        setInternalPage(lastPage);
      }
    }
  }, [activePage, onPageChange, screens.length]);

  const activeScreen = screens[activePage];
  const footerActions = [...actions, ...(activeScreen?.actions ?? [])];
  const stepColour = theme?.primaryColour ?? "#2185d0";
  const hasMultipleScreens = screens.length > 1;

  const nextPageAvailable = activePage < screens.length - 1;
  const prevPageAvailable = activePage !== 0;
  const nextDisabled = !nextPageAvailable || !!activeScreen?.disableNext;
  const headerTitle = activeScreen?.title || title;

  if (!screens.length) return null;
  return (
    <Modal open={open} onClose={onClose}>
      {headerTitle ? <ModalHeader>{headerTitle}</ModalHeader> : null}
      <ModalContent scrolling={scrolling}>
        <div>{activeScreen?.content}</div>
      </ModalContent>
      <ModalActions>
        <ActionsRow>
          <NavGroup>
            {hasMultipleScreens && prevPageAvailable ? (
              <Button
                onClick={() => setActivePage((prevPage) => prevPage - 1)}
                variant="outline"
              >
                <Icon icon={ChevronLeftIcon} />
                Back
              </Button>
            ) : null}
            {hasMultipleScreens ? (
              <StepProgress>
                <StepCount>
                  {activePage + 1} of {screens.length}
                </StepCount>
                <StepDots>
                  {screens.map((screen, index) => (
                    <StepDotButton
                      key={`${screen.title}-${index}`}
                      isActive={activePage === index}
                      isCompleted={index < activePage}
                      colour={stepColour}
                      canNavigate={index <= activePage}
                      onClick={() => {
                        if (index <= activePage) {
                          setActivePage(index);
                        }
                      }}
                    />
                  ))}
                </StepDots>
              </StepProgress>
            ) : null}
          </NavGroup>

          <ActionGroup>
            {footerActions.map((action) => (
              <Button
                key={action.label}
                disabled={action.disabled}
                loading={action.loading}
                onClick={action.onClick}
                color={action.primary ? "primary" : undefined}
              >
                <Icon icon={action.icon} />
                {action.label}
              </Button>
            ))}
            {hasMultipleScreens && nextPageAvailable ? (
              <Button
                disabled={nextDisabled}
                onClick={() => setActivePage((prevPage) => prevPage + 1)}
                color="primary"
              >
                Next
                <Icon icon={ChevronRightIcon} />
              </Button>
            ) : null}
          </ActionGroup>
        </ActionsRow>
      </ModalActions>
    </Modal>
  );
};

export default ModalCarousel;
